<main class="pdash">

  <header class="pdash__header">
    <div class="pdash__heading">
      <h1>{{ 'publisherDashboard.title' | translate }}</h1>
      <p>{{ 'publisherDashboard.subtitle' | translate }}</p>
    </div>

    <div class="pdash__filters">
      <div class="pdash__provider" *ngIf="showProviderSelect">
        <label>{{ 'publisherDashboard.providerFilter.label' | translate }}</label>
        <app-custom-select
          [(ngModel)]="selectedProviderId"
          (ngModelChange)="onProviderChange()"
          [options]="providerOptions"
          [searchable]="true"
          [placeholder]="'publisherDashboard.providerFilter.all' | translate">
        </app-custom-select>
      </div>

      <div class="pdash__range" role="group" [attr.aria-label]="'publisherDashboard.timeRange.label' | translate">
        <button type="button"
                *ngFor="let days of timeRangeOptions"
                class="pdash__range-btn"
                [class.active]="selectedTimeRange === days"
                (click)="selectTimeRange(days)">
          {{ 'publisherDashboard.timeRange.days' | translate:{ count: days } }}
        </button>
      </div>
    </div>
  </header>

  <!-- Overview: stat cards + primary charts -->
  <section class="pdash__section" [class.is-loading]="loadingOverview">

    <div class="pdash__error" *ngIf="overviewError">
      <p>{{ 'publisherDashboard.status.error' | translate }}</p>
      <button type="button" class="pdash__retry" (click)="retryOverview()">{{ 'publisherDashboard.actions.retry' | translate }}</button>
    </div>

    <ng-container *ngIf="!overviewError">
      <div class="pdash__stats">
        <div class="pdash-stat" *ngFor="let card of statCards" [class.pdash-stat--stale]="!loadingOverview && staleDates[card.key]">
          <span class="pdash-stat__label">{{ card.label }}</span>
          <ng-container *ngIf="!loadingOverview" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates[card.key] }"></ng-container>
          <span class="pdash-stat__value" *ngIf="!loadingOverview">{{ card.value }}</span>
          <span class="pdash-stat__value pdash-stat__value--placeholder" *ngIf="loadingOverview">&nbsp;</span>
        </div>
      </div>

      <div class="pdash__grid">
        <div class="pdash-card pdash-card--wide">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.charts.trends' | translate }}</h3>
              <ng-container *ngIf="!loadingOverview" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['trends'] }"></ng-container>
            </div>
          </div>
          <div class="pdash-card__body pdash-card__body--chart">
            <canvas #trendsCanvas></canvas>
            <div class="pdash-empty" *ngIf="!loadingOverview && !trendsHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
          </div>
        </div>
      </div>
    </ng-container>
  </section>

  <!-- Advanced: geo, age/gender, provider breakdown, technologies, tags -->
  <section class="pdash__section" [class.is-loading]="loadingAdvanced">

    <div class="pdash__error" *ngIf="advancedError">
      <p>{{ 'publisherDashboard.status.error' | translate }}</p>
      <button type="button" class="pdash__retry" (click)="retryAdvanced()">{{ 'publisherDashboard.actions.retry' | translate }}</button>
    </div>

    <ng-container *ngIf="!advancedError">
      <div class="pdash__grid pdash__grid--halves">
        <div class="pdash-card">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.sections.geo' | translate }}</h3>
              <ng-container *ngIf="!loadingAdvanced" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['geo'] }"></ng-container>
            </div>
          </div>
          <div class="pdash-card__body">
            <div class="pdash-empty" *ngIf="!loadingAdvanced && !geoHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
            <div class="pdash-ranked" *ngIf="geoHasData">
              <div class="pdash-ranked__row" *ngFor="let row of geoRows">
                <span class="pdash-ranked__name" [title]="row.code">{{ row.name }}</span>
                <div class="pdash-ranked__bar-track">
                  <div class="pdash-ranked__bar-fill" [style.width.%]="row.pct"></div>
                </div>
                <span class="pdash-ranked__value">{{ formatValue(row.value) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="pdash-card">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.sections.ageGender' | translate }}</h3>
              <ng-container *ngIf="!loadingAdvanced" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['ageGender'] }"></ng-container>
            </div>
            <div class="pdash-toggle-group" *ngIf="ageGenderHasData">
              <button type="button" [class.active]="selectedGenderView === 'total'" (click)="selectGenderView('total')">{{ 'publisherDashboard.ageGender.total' | translate }}</button>
              <button type="button" [class.active]="selectedGenderView === 'male'" (click)="selectGenderView('male')">{{ 'publisherDashboard.ageGender.male' | translate }}</button>
              <button type="button" [class.active]="selectedGenderView === 'female'" (click)="selectGenderView('female')">{{ 'publisherDashboard.ageGender.female' | translate }}</button>
            </div>
          </div>
          <div class="pdash-card__body">
            <div class="pdash-empty" *ngIf="!loadingAdvanced && !ageGenderHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
            <div class="pdash-age-gender" *ngIf="ageGenderHasData">
              <div class="pdash-age-gender__donut">
                <canvas #ageDonutCanvas></canvas>
              </div>
              <div class="pdash-age-gender__bars">
                <canvas #ageBarCanvas></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pdash__grid">
        <div class="pdash-card pdash-card--wide">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.sections.providerBreakdown' | translate }}</h3>
              <ng-container *ngIf="!loadingAdvanced" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['providerBreakdown'] }"></ng-container>
            </div>
          </div>
          <div class="pdash-card__body">
            <div class="pdash-empty" *ngIf="!loadingAdvanced && !providerHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
            <div class="pdash-treemap-row" *ngIf="providerHasData">
              <div class="pdash-treemap-row__col"
                   *ngFor="let row of providerRows"
                   [style.flex-grow]="row.pct || 0.5"
                   [style.background]="row.color"
                   [title]="row.label + ': ' + formatValue(row.value) + ' (' + row.pct + '%)'">
                <span class="pdash-treemap-row__label">{{ row.label }}</span>
                <span class="pdash-treemap-row__value">{{ formatValue(row.value) }} <span class="pdash-treemap-row__divider">|</span> {{ row.pct }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pdash__grid pdash__grid--thirds">
        <div class="pdash-card">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.technologies.browser' | translate }}</h3>
              <ng-container *ngIf="!loadingAdvanced" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['browser'] }"></ng-container>
            </div>
          </div>
          <div class="pdash-card__body pdash-card__body--chart">
            <canvas #browserPieCanvas></canvas>
            <div class="pdash-empty" *ngIf="!loadingAdvanced && !browserHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
          </div>
        </div>
        <div class="pdash-card">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.technologies.device' | translate }}</h3>
              <ng-container *ngIf="!loadingAdvanced" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['device'] }"></ng-container>
            </div>
          </div>
          <div class="pdash-card__body pdash-card__body--chart">
            <canvas #devicePieCanvas></canvas>
            <div class="pdash-empty" *ngIf="!loadingAdvanced && !deviceHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
          </div>
        </div>
        <div class="pdash-card">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.technologies.os' | translate }}</h3>
              <ng-container *ngIf="!loadingAdvanced" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['os'] }"></ng-container>
            </div>
          </div>
          <div class="pdash-card__body pdash-card__body--chart">
            <canvas #osPieCanvas></canvas>
            <div class="pdash-empty" *ngIf="!loadingAdvanced && !osHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
          </div>
        </div>
      </div>

      <div class="pdash__grid">
        <div class="pdash-card pdash-card--wide">
          <div class="pdash-card__header">
            <div class="pdash-card__title">
              <h3>{{ 'publisherDashboard.sections.tags' | translate }}</h3>
              <ng-container *ngIf="!loadingAdvanced" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['tags'] }"></ng-container>
            </div>
          </div>
          <div class="pdash-card__body">
            <div class="pdash-empty" *ngIf="!loadingAdvanced && !tagsHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
            <div class="pdash-tagcloud" *ngIf="tagsHasData">
              <span class="pdash-tagcloud__word"
                    *ngFor="let word of tagWords"
                    [style.font-size.px]="word.fontSize"
                    [style.color]="word.color"
                    [title]="word.label + ': ' + formatValue(word.value) + ' (' + word.pct + '%)'">
                {{ word.label }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </section>

  <!-- TNCIDs with Third Parties/Other IDs sync: shown large, at the end of the page -->
  <section class="pdash__section" [class.is-loading]="loadingOverview" *ngIf="!overviewError">
    <div class="pdash__grid">
      <div class="pdash-card pdash-card--wide pdash-card--tall">
        <div class="pdash-card__header">
          <div class="pdash-card__title">
            <h3>{{ 'publisherDashboard.charts.exportIds' | translate }}</h3>
            <ng-container *ngIf="!loadingOverview" [ngTemplateOutlet]="staleBadge" [ngTemplateOutletContext]="{ $implicit: staleDates['externalIds'] }"></ng-container>
          </div>
        </div>
        <div class="pdash-card__body pdash-card__body--chart">
          <canvas #externalIdsCanvas></canvas>
          <div class="pdash-empty" *ngIf="!loadingOverview && !externalIdsHasData">{{ 'publisherDashboard.status.noData' | translate }}</div>
        </div>
      </div>
    </div>
  </section>

  <!-- Warning shown on any card whose data is older than yesterday (nightly ETL not landed). -->
  <ng-template #staleBadge let-date>
    <span class="pdash-stale" *ngIf="date" role="status"
          [title]="'publisherDashboard.stale.tooltip' | translate:{ date: formatDataDate(date) }">
      <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
        <path fill="currentColor" d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z"/>
      </svg>
      {{ 'publisherDashboard.stale.label' | translate:{ date: formatDataDate(date) } }}
    </span>
  </ng-template>

</main>
